import qs from 'qs';
import { internalImageRemotePatterns } from './constants/image-remote-patterns';
import { env } from '@/utils/env';

type ImageLoaderProps = {
  /** Original image path */
  src: string;

  /**
   * Maximum width of the image in pixels. Exact behavior depends on the fit mode.
   * When 'auto' is used, it automatically calculates the optimal width based on browser and device information (supported only by Chromium browsers).
   */
  width: number | 'auto';

  /**
   * Quality for images in JPEG, WebP, and AVIF formats (1-100).
   * Values between 50 (low quality, small file size) and 90 (high quality, large file size) are most useful. Default is 75.
   * String values 'high', 'medium-high', 'medium-low', or 'low' can also be used.
   */
  quality?: number | 'high' | 'medium-high' | 'medium-low' | 'low';

  /**
   * Whether to preserve animation frames from input files. Default is true.
   * Setting to false reduces animations to still images.
   */
  anim?: boolean;

  /**
   * Background color to add underneath the image. Applies to images with transparency (e.g., PNG)
   * and images resized with fit=pad. Accepts CSS color values.
   */
  background?: string;

  /**
   * Blur radius for the image. Values range from 1 (slight blur) to 250 (maximum blur).
   */
  blur?: number;

  /**
   * Adjusts brightness by a factor. 1.0 equals no change, 0.5 equals half brightness, and 2.0 equals twice as bright.
   */
  brightness?: number;

  /**
   * Slightly reduces latency on a cache miss by selecting the quickest-to-compress file format,
   * at the cost of increased file size and lower image quality.
   */
  compression?: 'fast';

  /**
   * Adjusts contrast by a factor. 1.0 equals no change, 0.5 equals low contrast, and 2.0 equals high contrast.
   */
  contrast?: number;

  /**
   * Device Pixel Ratio. Default is 1.
   * A multiplier for width/height to make it easier to specify higher-DPI sizes.
   */
  dpr?: number;

  /**
   * Affects interpretation of width and height. All resizing modes preserve aspect ratio.
   * - scale-down: Similar to contain, but the image is never enlarged.
   * - contain: Resizes to be as large as possible within the given width/height while preserving aspect ratio.
   * - cover: Resizes to fill the entire area of width/height, cropping if necessary.
   * - crop: Shrinks and crops the image to fit within the specified width/height area.
   * - pad: Resizes to maximum size that fits within width/height, then fills remaining area with background color.
   */
  fit?: 'scale-down' | 'contain' | 'cover' | 'crop' | 'pad';

  /**
   * Flips the image horizontally, vertically, or both.
   * - h: Flips the image horizontally.
   * - v: Flips the image vertically.
   * - hv: Flips the image both horizontally and vertically.
   */
  flip?: 'h' | 'v' | 'hv';

  /**
   * Specifies the output image format.
   * - auto: Serves WebP or AVIF format to browsers that support it.
   * - avif: Generates images in AVIF format if possible (with WebP as fallback).
   * - webp: Generates images in Google WebP format.
   * - jpeg: Generates images in interlaced progressive JPEG format.
   * - baseline-jpeg: Generates images in baseline sequential JPEG format.
   * - json: Outputs information about the image in JSON format instead of an image.
   */
  format?: 'auto' | 'avif' | 'webp' | 'jpeg' | 'baseline-jpeg' | 'json';

  /**
   * Adjusts exposure by a factor. 1.0 equals no change, 0.5 darkens the image, and 2.0 lightens the image.
   */
  gamma?: number;

  /**
   * When cropping with fit: "cover" and fit: "crop", defines the side or point that should not be cropped.
   * - auto: Selects focal point based on saliency detection.
   * - side: A side ("left", "right", "top", "bottom") or coordinates can be specified.
   */
  gravity?: 'auto' | 'left' | 'right' | 'top' | 'bottom' | string;

  /**
   * Maximum height of the image in pixels. Exact behavior depends on the fit mode.
   */
  height?: number;

  /**
   * Controls the amount of invisible metadata (EXIF data) that should be preserved.
   * - copyright: Discards all EXIF metadata except copyright tag.
   * - keep: Preserves most EXIF metadata, including GPS location if present.
   * - none: Discards all invisible EXIF and C2PA metadata.
   */
  metadata?: 'copyright' | 'keep' | 'none';

  /**
   * In case of a fatal error that prevents the image from being resized,
   * redirects to the unresized source image URL.
   */
  onerror?: 'redirect';

  /**
   * Rotates the image by the specified number of degrees (90, 180, or 270).
   * Width and height options refer to axes after rotation.
   */
  rotate?: 90 | 180 | 270;

  /**
   * Adjusts saturation by a factor. 1.0 equals no change, 0.5 equals half saturation, and 2.0 equals twice as saturated.
   * A value of 0 will convert the image to grayscale.
   */
  saturation?: number;

  /**
   * Specifies the strength of sharpening filter to apply to the image.
   * Values range from 0 (no sharpening, default) to 10 (maximum).
   */
  sharpen?: number;

  /**
   * Allows overriding the quality value whenever a slow connection is detected.
   * Uses the same options as the quality parameter.
   */
  'slow-connection-quality'?: number | 'high' | 'medium-high' | 'medium-low' | 'low';

  /**
   * Specifies the number of pixels to cut off on each side. Allows removal of borders or cutting out specific fragments.
   * Format: "top;right;bottom;left"
   */
  trim?: string;
};

/**
 *
 * Note: https://developers.cloudflare.com/images/transform-images/transform-via-url/
 */
export default function cloudflareImageLoader({ src, width, quality = 75, ...otherOptions }: ImageLoaderProps) {
  const options = { width, quality, ...otherOptions };

  const externalImageRemoteList = new Set(internalImageRemotePatterns.map((pattern) => pattern.hostname));

  const hostname = new URL(src).hostname;

  const isExternalImage = !externalImageRemoteList.has(hostname) || !hostname.includes('skin-seoul.com');

  if (isExternalImage) {
    return src;
  }

  return `${env('NEXT_PUBLIC_ENV_CLOUDFLARE_IMAGE_ORIGIN')}/cdn-cgi/image/${qs.stringify(options)}/${src}`;
}
